/**
 * Effect Presets Browser
 * Browse, download, and share effect chain presets with the community
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Search,
  Download,
  Share2,
  Star,
  TrendingUp,
  Filter,
  Grid,
  List,
  X,
  Check,
  Loader2,
  Users,
  Clock,
  Sparkles,
  Crown,
  Save,
  Edit2,
  Trash2,
  Globe,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Effect preset categories
const CATEGORIES = {
  all: { name: 'All', icon: '🎵', color: 'zinc' },
  vocal: { name: 'Vocal', icon: '🎤', color: 'pink' },
  guitar: { name: 'Guitar', icon: '🎸', color: 'orange' },
  drums: { name: 'Drums', icon: '🥁', color: 'amber' },
  bass: { name: 'Bass', icon: '🎸', color: 'emerald' },
  synth: { name: 'Synth', icon: '🎹', color: 'violet' },
  mastering: { name: 'Mastering', icon: '📀', color: 'cyan' },
  creative: { name: 'Creative', icon: '✨', color: 'indigo' },
  custom: { name: 'Custom', icon: '💾', color: 'slate' },
};

// Star rating component
const StarRating = ({ rating, count, size = 'sm' }) => {
  const stars = Math.round(rating);
  const sizeClass = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
  
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            className={`${sizeClass} ${i <= stars ? 'text-amber-400 fill-amber-400' : 'text-zinc-600'}`}
          />
        ))}
      </div>
      {count > 0 && (
        <span className="text-[10px] text-zinc-500">({count})</span>
      )}
    </div>
  );
};

// Preset card component
const PresetCard = ({ 
  preset, 
  onDownload, 
  onApply,
  onShare,
  onDelete,
  isOwned,
  isDownloading,
  isCurrentUser 
}) => {
  const category = CATEGORIES[preset.category] || CATEGORIES.custom;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-xl border bg-zinc-900/50 hover:bg-zinc-800/50 transition-all ${
        `border-${category.color}-500/20 hover:border-${category.color}-500/40`
      }`}
      data-testid={`preset-card-${preset.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl bg-${category.color}-500/20`}>
          {preset.icon || category.icon}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-white truncate">{preset.name}</h4>
            {preset.creator_name === 'String Sauce' && (
              <span className="px-1.5 py-0.5 text-[10px] bg-orange-500/20 text-orange-400 rounded">
                OFFICIAL
              </span>
            )}
          </div>
          
          <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{preset.description}</p>
          
          <div className="flex items-center gap-3 mt-2">
            {preset.rating > 0 && (
              <StarRating rating={preset.rating} count={preset.rating_count} />
            )}
            <span className="text-xs text-zinc-500 flex items-center gap-1">
              <Download className="w-3 h-3" />
              {preset.downloads || 0}
            </span>
            <span className="text-xs text-zinc-500">
              by {preset.creator_name || 'Unknown'}
            </span>
          </div>
          
          {/* Tags */}
          {preset.tags && preset.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {preset.tags.slice(0, 3).map(tag => (
                <span key={tag} className="px-1.5 py-0.5 text-[10px] bg-zinc-800 text-zinc-400 rounded">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800">
        <Button
          size="sm"
          onClick={() => onApply?.(preset)}
          className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
          data-testid={`apply-preset-${preset.id}`}
        >
          <Sparkles className="w-3 h-3 mr-1" />
          Apply
        </Button>
        
        {!isOwned && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDownload?.(preset.id)}
            disabled={isDownloading}
            className="border-zinc-700"
            data-testid={`download-preset-${preset.id}`}
          >
            {isDownloading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Download className="w-3 h-3" />
            )}
          </Button>
        )}
        
        {isCurrentUser && (
          <>
            {!preset.is_shared ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onShare?.(preset)}
                className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
              >
                <Share2 className="w-3 h-3" />
              </Button>
            ) : (
              <span className="text-xs text-emerald-400 flex items-center gap-1">
                <Globe className="w-3 h-3" />
                Shared
              </span>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDelete?.(preset.id)}
              className="text-zinc-400 hover:text-red-400"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </>
        )}
      </div>
    </motion.div>
  );
};

// Share modal
const ShareModal = ({ preset, onClose, onShare }) => {
  const [tags, setTags] = useState(preset.tags?.join(', ') || '');
  const [isSharing, setIsSharing] = useState(false);
  
  const handleShare = async () => {
    setIsSharing(true);
    try {
      await onShare(preset.id, {
        is_public: true,
        price: 0,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      toast.success('Preset shared with the community!');
      onClose();
    } catch (err) {
      toast.error('Failed to share preset');
    } finally {
      setIsSharing(false);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md bg-zinc-900 rounded-2xl border border-zinc-700 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-zinc-800">
          <h3 className="text-lg font-semibold text-white">Share "{preset.name}"</h3>
          <p className="text-sm text-zinc-400">Share your effect chain with the community</p>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Tags (comma separated)</label>
            <Input
              value={tags}
              onChange={e => setTags(e.target.value)}
              placeholder="vocal, warm, professional"
              className="bg-zinc-800 border-zinc-700"
            />
          </div>
          
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <p className="text-sm text-emerald-400">
              Your preset will be shared for free. Other users can download and use it.
            </p>
          </div>
        </div>
        
        <div className="px-6 py-4 border-t border-zinc-800 flex gap-3">
          <Button variant="ghost" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleShare}
            disabled={isSharing}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600"
          >
            {isSharing ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Share2 className="w-4 h-4 mr-2" />
            )}
            Share Preset
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Save preset modal
const SavePresetModal = ({ effects, onClose, onSave, userId }) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('custom');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a preset name');
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave({
        name,
        category,
        description,
        settings: effects,
      });
      toast.success('Preset saved!');
      onClose();
    } catch (err) {
      toast.error('Failed to save preset');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md bg-zinc-900 rounded-2xl border border-zinc-700 overflow-hidden"
        data-testid="save-preset-modal"
      >
        <div className="px-6 py-4 border-b border-zinc-800">
          <h3 className="text-lg font-semibold text-white">Save Effect Preset</h3>
          <p className="text-sm text-zinc-400">Save your current effect chain as a reusable preset</p>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Preset Name</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="My Awesome Preset"
              className="bg-zinc-800 border-zinc-700"
              data-testid="preset-name-input"
            />
          </div>
          
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Category</label>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(CATEGORIES).filter(([k]) => k !== 'all').map(([key, cat]) => (
                <button
                  key={key}
                  onClick={() => setCategory(key)}
                  className={`p-2 rounded-lg text-center transition-all ${
                    category === key
                      ? `bg-${cat.color}-500/20 border border-${cat.color}-500/50`
                      : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-500'
                  }`}
                >
                  <span className="text-lg">{cat.icon}</span>
                  <span className="block text-[10px] text-zinc-400 mt-0.5">{cat.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Description</label>
            <Input
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe your effect chain..."
              className="bg-zinc-800 border-zinc-700"
            />
          </div>
        </div>
        
        <div className="px-6 py-4 border-t border-zinc-800 flex gap-3">
          <Button variant="ghost" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className="flex-1 bg-orange-500 hover:bg-orange-600"
            data-testid="save-preset-submit"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Preset
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main browser component
export const EffectPresetsBrowser = ({
  isOpen,
  onClose,
  onApplyPreset,
  currentEffects,
  userId,
}) => {
  const [activeTab, setActiveTab] = useState('browse'); // browse, my-presets
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('popular');
  const [presets, setPresets] = useState([]);
  const [myPresets, setMyPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [sharePreset, setSharePreset] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Fetch community presets
  const fetchPresets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        sort,
        ...(category !== 'all' && { category }),
        ...(search && { search }),
      });
      
      const response = await fetch(`${API_URL}/api/effect-presets/community?${params}`);
      const data = await response.json();
      setPresets(data.presets || []);
    } catch (err) {
      console.error('Error fetching presets:', err);
      toast.error('Failed to load presets');
    } finally {
      setLoading(false);
    }
  }, [category, search, sort]);

  // Fetch user's presets
  const fetchMyPresets = useCallback(async () => {
    if (!userId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/effect-presets/user/${userId}`);
      const data = await response.json();
      setMyPresets(data.presets || []);
    } catch (err) {
      console.error('Error fetching my presets:', err);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen) {
      fetchPresets();
      fetchMyPresets();
    }
  }, [isOpen, fetchPresets, fetchMyPresets]);

  // Download preset
  const handleDownload = async (presetId) => {
    setDownloadingId(presetId);
    try {
      const response = await fetch(`${API_URL}/api/effect-presets/${presetId}/download?user_id=${userId}`, {
        method: 'POST',
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Preset downloaded to your library');
        fetchMyPresets();
      }
    } catch (err) {
      toast.error('Failed to download preset');
    } finally {
      setDownloadingId(null);
    }
  };

  // Share preset
  const handleShare = async (presetId, shareData) => {
    const response = await fetch(`${API_URL}/api/effect-presets/${presetId}/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, ...shareData }),
    });
    
    if (!response.ok) throw new Error('Failed to share');
    
    fetchMyPresets();
    fetchPresets();
  };

  // Save new preset
  const handleSavePreset = async (presetData) => {
    const response = await fetch(`${API_URL}/api/effect-presets/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        preset: presetData,
      }),
    });
    
    if (!response.ok) throw new Error('Failed to save');
    
    fetchMyPresets();
  };

  // Delete preset
  const handleDelete = async (presetId) => {
    if (!window.confirm('Delete this preset?')) return;
    
    try {
      await fetch(`${API_URL}/api/effect-presets/${presetId}?user_id=${userId}`, {
        method: 'DELETE',
      });
      toast.success('Preset deleted');
      fetchMyPresets();
    } catch (err) {
      toast.error('Failed to delete preset');
    }
  };

  if (!isOpen) return null;

  const displayPresets = activeTab === 'browse' ? presets : myPresets;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={e => e.stopPropagation()}
          className="w-full max-w-4xl max-h-[85vh] bg-zinc-900 rounded-2xl border border-zinc-700 overflow-hidden flex flex-col"
          data-testid="effect-presets-browser"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-zinc-800 bg-gradient-to-r from-orange-500/10 via-amber-500/10 to-yellow-500/10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                  Effect Presets
                </h2>
                <p className="text-sm text-zinc-400">Browse and share effect chain presets</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => setShowSaveModal(true)}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="save-current-preset"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save Current
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setActiveTab('browse')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'browse'
                    ? 'bg-orange-500/20 text-orange-400'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                <Globe className="w-4 h-4 inline mr-1" />
                Browse Community
              </button>
              <button
                onClick={() => setActiveTab('my-presets')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'my-presets'
                    ? 'bg-orange-500/20 text-orange-400'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                <Users className="w-4 h-4 inline mr-1" />
                My Presets ({myPresets.length})
              </button>
            </div>
          </div>

          {/* Filters */}
          {activeTab === 'browse' && (
            <div className="px-6 py-3 border-b border-zinc-800 flex items-center gap-4">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search presets..."
                  className="pl-9 bg-zinc-800 border-zinc-700"
                />
              </div>
              
              <div className="flex gap-1 overflow-x-auto">
                {Object.entries(CATEGORIES).map(([key, cat]) => (
                  <button
                    key={key}
                    onClick={() => setCategory(key)}
                    className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-all ${
                      category === key
                        ? `bg-${cat.color}-500/20 text-${cat.color}-400 border border-${cat.color}-500/50`
                        : 'text-zinc-400 hover:text-white bg-zinc-800'
                    }`}
                  >
                    {cat.icon} {cat.name}
                  </button>
                ))}
              </div>
              
              <select
                value={sort}
                onChange={e => setSort(e.target.value)}
                className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-300"
              >
                <option value="popular">Most Popular</option>
                <option value="newest">Newest</option>
                <option value="rating">Highest Rated</option>
              </select>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
              </div>
            ) : displayPresets.length === 0 ? (
              <div className="text-center py-12">
                <Sparkles className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                <h3 className="text-lg text-zinc-400">No presets found</h3>
                <p className="text-sm text-zinc-500 mt-1">
                  {activeTab === 'browse' 
                    ? 'Try adjusting your search or filters'
                    : 'Save your first preset to get started'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {displayPresets.map(preset => (
                  <PresetCard
                    key={preset.id}
                    preset={preset}
                    onApply={onApplyPreset}
                    onDownload={handleDownload}
                    onShare={setSharePreset}
                    onDelete={handleDelete}
                    isOwned={myPresets.some(p => p.id === preset.id || p.original_id === preset.id)}
                    isDownloading={downloadingId === preset.id}
                    isCurrentUser={preset.creator_id === userId}
                  />
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
      
      {/* Share Modal */}
      <AnimatePresence>
        {sharePreset && (
          <ShareModal
            preset={sharePreset}
            onClose={() => setSharePreset(null)}
            onShare={handleShare}
          />
        )}
      </AnimatePresence>
      
      {/* Save Modal */}
      <AnimatePresence>
        {showSaveModal && (
          <SavePresetModal
            effects={currentEffects}
            onClose={() => setShowSaveModal(false)}
            onSave={handleSavePreset}
            userId={userId}
          />
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
};

export default EffectPresetsBrowser;
